<template>
  <div class="details-section">
    <div class="details-info" @click="toggle">
      <div class="short-info" :class="{active: state}">
        <div class="main-info" v-if="flightDetails">
          <strong> {{ flightDetails.departure }} - {{ flightDetails.arrival }} </strong>
          {{ flightDetails.duration }} flight
        </div>
        <div class="details">
          <strong>{{ flightDetails.directions }} {{ flightDetails.details }}, {{ flightDetails.numberOfStops }}</strong>
          with
          {{ flightDetails.airlines }}
          <TriangleIcon class="inline-carret" :class="{active: state}" />
        </div>
      </div>

      <div class="itinerary-info" :class="{active: state}">
        <div class="itinerary-section" v-for="segment in segments" :key="segment.id">
          <div class="main-itinerary-details">
            <div class="main-itinerary-details__left-border"></div>
            <div class="departure">
              <strong>{{ segment.departureDate }}</strong> {{ segment.origin }}
            </div>
            <div class="travel">
              travel time <strong>{{ segment.duration }}</strong>
            </div>
            <div class="departure">
              <strong>{{ segment.arrivalDate }}</strong> {{ segment.dest }}
            </div>
          </div>
          <div class="layover" v-if="segment.layover">
            <span> Layover {{ segment.layover }} </span>
          </div>
        </div>
      </div>
    </div>

    <!-- <div class="price" v-if="!hidePrice">
      <div class="price-tag">{{ direction }}</div>
      {{ flightDetails.price }}
    </div> -->
  </div>
</template>

<script>
import {fixDate} from '@/store/state/fix-solution-dates';
import {getDuration} from '@/store/state/utils';
import {computed, defineComponent, ref} from '@vue/composition-api';
import TriangleIcon from '../../svg/triangle.vue';
import {getDateParts} from './useMainFlightDetails';

export default defineComponent({
  components: {TriangleIcon},
  props: {
    flightDetails: Object,
    direction: String,
    hidePrice: {
      type: Boolean,
      default: false,
    },
  },
  setup(props) {
    const state = ref(false);
    const segments = computed(() => {
      return props.flightDetails.segments.map((x, i) => {
        const departureTime = new Date(fixDate(x.departure_timestamp, x.origin_timezone) * 1000);
        const duration = getDuration(x.arrival_timestamp * 1000 - x.departure_timestamp * 1000);
        const arrivalTime = new Date(fixDate(x.arrival_timestamp, x.destination_timezone) * 1000);
        const {date: departureLocalDate, time: departureLocalTime} = getDateParts(departureTime);
        const {date: arrivalLocalDate, time: arrivalLocalTime} = getDateParts(arrivalTime);

        const nextSegment = props.flightDetails.segments[i + 1];
        const nextDeparture = fixDate(nextSegment?.departure_timestamp || 0, nextSegment?.origin_timezone) * 1000;
        const layoverDuration = nextDeparture.valueOf() - arrivalTime.valueOf();
        const layover = getDuration(layoverDuration);

        return {
          id: i,
          departureDate: `${departureLocalDate}, ${departureLocalTime}`,
          arrivalDate: `${arrivalLocalDate}, ${arrivalLocalTime}`,
          origin: x.origin,
          dest: x.destination,
          duration,
          layover: nextSegment ? layover : '',
        };
      });
    });

    const toggle = () => {
      state.value = !state.value;
    };

    return {segments, toggle, state};
  },
});
</script>

<style lang="sass" scoped>
.details-section
  position: relative
  display: flex
  justify-content: space-between
  margin: 10px 0

  &:before
    position: absolute
    content: ''
    background: #f1f1f1
    height: 1px
    width: 100%
    top: -5px
    display: block

.main-info
  font-style: normal
  font-size: 12px
  line-height: 118.14%
  letter-spacing: -0.005em
  color: #071d3b

.details
  font-style: normal
  font-weight: 400
  font-size: 12px
  line-height: 118.14%
  letter-spacing: -0.005em
  color: rgba(7, 29, 59, 0.4)

.price
  font-style: normal
  font-weight: 400
  font-size: 16px
  line-height: 118.14%
  text-align: right
  letter-spacing: -0.005em
  color: #071d3b

  &-tag
    font-style: normal
    font-weight: 400
    font-size: 10px
    line-height: 118.14%
    text-align: right
    letter-spacing: -0.005em

    color: rgba(7, 29, 59, 0.37)

.inline-carret
  color: #A3ABB6
  position: relative
  top: 1px
  font-size: 10px
  transition: transform .2s linear

  &.active
    transform: rotate(60deg)
</style>

<style scoped>
.details-info {
  position: relative;
}

.short-info {
  padding-right: 15px;
  cursor: pointer;
}

.itinerary-info {
  margin: 10px 0;
  max-height: 0;
  overflow: hidden;
  transition: max-height 0.5s linear;
  background: #fff;
}

.itinerary-info.active {
  max-height: 300px;
}

.main-itinerary-details {
  padding-left: 15px;
  position: relative;
}

.main-itinerary-details__left-border {
  position: absolute;
  left: 4px;
  top: 12px;
  height: calc(100% - 23px);
  width: 2px;
  background: #9ca5b0;
}

.departure {
  font-style: normal;
  font-weight: 400;
  font-size: 12px;
  line-height: 118.14%;
  letter-spacing: -0.005em;
  color: #071d3b;
}

.travel {
  font-style: normal;
  font-weight: 400;
  font-size: 11px;
  line-height: 118.14%;
  letter-spacing: -0.005em;
  color: rgba(7, 29, 59, 0.4);
  text-transform: capitalize;
  margin: 5px 0;
}

.layover {
  font-style: normal;
  font-weight: 400;
  font-size: 11px;
  line-height: 118.14%;
  text-align: center;
  letter-spacing: -0.005em;
  color: rgba(7, 29, 59, 0.4);
  margin: 7px 0;
  position: relative;
}

.layover > span {
  display: inline-block;
  background: #fff;
  z-index: 2;
  position: relative;
  padding: 0 10px;
}

.layover:before {
  content: '';
  position: absolute;
  width: 90%;
  left: 5%;
  top: calc(50% - 1px);
  height: 2px;
  background: #ededed;
  z-index: 0;
}

.main-itinerary-details:before,
.main-itinerary-details:after {
  content: '';
  position: absolute;
  left: 0;
  top: 3px;
  border: 2px solid #9ca5b0;
  width: 10px;
  height: 10px;
  border-radius: 50%;
}

.main-itinerary-details:after {
  top: auto;
  bottom: 3px;
}
</style>
